import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import { useDocumentTitle } from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import "./PredictionBotDashboard.scss";
import kalshiLogo from "./kalshi-logo.svg";
import polymarketLogo from "./polymarket-logo.svg";

function toPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${(num * 100).toFixed(2)}%`;
}

function toAbsPercent(value) {
  const num = Math.abs(Number(value));
  if (!Number.isFinite(num)) return "-";
  return `${(num * 100).toFixed(2)}%`;
}

function toNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString();
}

function toCompactUsd(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  if (num < 1000) return `$${num.toFixed(0)}`;
  if (num < 1_000_000) return `$${(num / 1000).toFixed(num >= 100_000 ? 0 : 2)}k`;
  if (num < 1_000_000_000) return `$${(num / 1_000_000).toFixed(num >= 100_000_000 ? 0 : 2)}m`;
  return `$${(num / 1_000_000_000).toFixed(2)}b`;
}

function toCompactNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  if (num < 1000) return `${Math.round(num)}`;
  if (num < 1_000_000) return `${(num / 1000).toFixed(num >= 100_000 ? 0 : 1)}k`;
  return `${(num / 1_000_000).toFixed(num >= 100_000_000 ? 0 : 1)}m`;
}

function toPrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `$${num.toFixed(4)}`;
}

function toContracts(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function toUsd(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function toCents(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${Math.round(num * 100)}¢`;
}

function clampNumber(value, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

function roundToStep(value, step) {
  const parsed = Number(value);
  const stepValue = Number(step);
  if (!Number.isFinite(parsed) || !Number.isFinite(stepValue) || stepValue <= 0) return value;
  return Math.round(parsed / stepValue) * stepValue;
}

function DraggableNumber({
  value,
  min,
  max,
  step,
  onChange,
  formatter,
  className = "",
  title = "",
  parseInput,
}) {
  const dragStateRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    return () => {
      if (!dragStateRef.current) return;
      window.removeEventListener("mousemove", dragStateRef.current.onMove);
      window.removeEventListener("mouseup", dragStateRef.current.onUp);
      dragStateRef.current = null;
    };
  }, []);

  const handleMouseDown = (event) => {
    if (isEditing) return;
    event.preventDefault();
    const startValue = Number(value);
    if (!Number.isFinite(startValue)) return;
    const startY = event.clientY;
    const pxPerStep = 6;

    const onMove = (moveEvent) => {
      const deltaY = startY - moveEvent.clientY;
      const rawSteps =
        deltaY >= 0 ? Math.floor(deltaY / pxPerStep) : Math.ceil(deltaY / pxPerStep);
      const nextValue = clampNumber(
        roundToStep(startValue + rawSteps * step, step),
        min,
        max
      );
      onChange(nextValue);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      dragStateRef.current = null;
    };

    dragStateRef.current = { onMove, onUp };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const commitEdit = () => {
    const parsed = parseInput ? parseInput(editValue) : Number(editValue);
    if (Number.isFinite(parsed)) {
      const next = clampNumber(parsed, min, max);
      onChange(next);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type="number"
        className={`${className} strategy-drag-input`}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitEdit();
          if (e.key === "Escape") setIsEditing(false);
        }}
        autoFocus
      />
    );
  }

  return (
    <button
      type="button"
      className={className}
      onMouseDown={handleMouseDown}
      onDoubleClick={() => {
        setEditValue(String(value ?? ""));
        setIsEditing(true);
      }}
      title={title}
    >
      {formatter ? formatter(value) : value}
    </button>
  );
}

function buildCumulativeTotals(rows = [], direction = "prefix") {
  const result = Array.isArray(rows) ? Array(rows.length).fill(0) : [];
  if (!Array.isArray(rows) || !rows.length) return result;

  if (direction === "suffix") {
    let running = 0;
    for (let idx = rows.length - 1; idx >= 0; idx -= 1) {
      const rowTotal = Number(rows[idx]?.total || 0);
      if (Number.isFinite(rowTotal) && rowTotal > 0) {
        running += rowTotal;
      }
      result[idx] = running;
    }
    return result;
  }

  let running = 0;
  for (let idx = 0; idx < rows.length; idx += 1) {
    const rowTotal = Number(rows[idx]?.total || 0);
    if (Number.isFinite(rowTotal) && rowTotal > 0) {
      running += rowTotal;
    }
    result[idx] = running;
  }
  return result;
}

function buildDepthPercentsFromCumulative(cumulativeTotals = []) {
  const maxTotal = cumulativeTotals.reduce(
    (max, value) => (Number.isFinite(value) && value > max ? value : max),
    0
  );
  if (!maxTotal) return cumulativeTotals.map(() => 0);
  return cumulativeTotals.map((value) => Math.min(100, (Number(value || 0) / maxTotal) * 100));
}

function renderDate(epochSeconds) {
  if (!epochSeconds) return "-";
  return new Date(epochSeconds * 1000).toLocaleString();
}

const LEAGUE_ABBREVIATIONS = {
  "basketball euroleague": "EuroLeague",
  "greece basket league": "Greece BL",
  "turkey bsl": "Turkey BSL",
  "japan b league": "Japan B.League",
  "france pro a": "Pro A",
  "italy serie a": "Serie A",
  "spain liga endesa": "Liga Endesa",
  "argentina lnb": "LNB",
  "china cba": "CBA",
  "korea kbl": "KBL",
  "vtb united league": "VTB",
  "aba league": "ABA",
  "ncaa-cbb": "NCAAB",
  cwbb: "CWBB",
  "nba-2026": "NBA",
  "wnba-2026": "WNBA",
};

function normalizeLeagueLabel(league) {
  const raw = String(league || "").trim();
  if (!raw) return "-";
  const normalized = raw.toLowerCase();
  if (LEAGUE_ABBREVIATIONS[normalized]) return LEAGUE_ABBREVIATIONS[normalized];
  if (normalized.includes("nba")) return "NBA";
  if (normalized.includes("wnba")) return "WNBA";
  if (normalized.includes("ncaa")) return "NCAAB";
  if (normalized.includes("euroleague")) return "EuroLeague";
  if (normalized.includes("basket")) return raw.replace(/^basketball\s+/i, "").trim() || "Basketball";
  return raw;
}

function toProviderLabel(provider) {
  const key = String(provider || "").trim().toLowerCase();
  if (key === "boltodds") return "Bolt";
  if (key === "the_odds_api") return "TheOddsAPI";
  if (key === "sportsdataio") return "SportsDataIO";
  return provider || "-";
}

function normalizeTeamKey(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenizeForMatch(text) {
  const normalized = normalizeTeamKey(text)
    .replace(/\bpts?\b/g, "points")
    .replace(/\basts?\b/g, "assists")
    .replace(/\brebs?\b/g, "rebounds")
    .replace(/\bpra\b/g, "points rebounds assists")
    .replace(/\b3pt\b/g, "threes")
    .replace(/\b3pm\b/g, "threes")
    .replace(/\bfg\b/g, "field goals");
  const stopwords = new Set([
    "over",
    "under",
    "yes",
    "no",
    "will",
    "score",
    "record",
    "more",
    "than",
    "less",
    "total",
    "player",
    "market",
    "game",
    "wins",
    "win",
    "scored",
    "by",
    "at",
    "points",
    "assists",
    "rebounds",
    "threes",
    "blocks",
    "steals",
    "turnovers",
  ]);
  return normalized
    .split(" ")
    .filter((token) => token.length > 1 && !stopwords.has(token));
}

function overlapScore(a, b) {
  const normalizedA = normalizeTeamKey(a);
  const normalizedB = normalizeTeamKey(b);
  if (!normalizedA || !normalizedB) return 0;
  if (normalizedA === normalizedB) return 1;
  if (normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA)) return 0.95;

  const aTokens = new Set(tokenizeForMatch(a));
  const bTokens = new Set(tokenizeForMatch(b));
  if (!aTokens.size || !bTokens.size) return 0;
  let overlap = 0;
  aTokens.forEach((token) => {
    if (bTokens.has(token)) overlap += 1;
  });
  return overlap / Math.max(aTokens.size, bTokens.size);
}

function parseExchangePropSignal(title = "", fallbackTexts = []) {
  const candidates = [title].concat(Array.isArray(fallbackTexts) ? fallbackTexts : []).filter(Boolean);
  let best = null;

  candidates.forEach((candidateText) => {
    const text = String(candidateText || "").trim();
    if (!text) return;
    const lower = text.toLowerCase();

    let side = null;
    let point = null;
    let participant = "";
    let marketHint = "";

    let match = lower.match(/\b(over|under)\b[^0-9-]*(-?\d+(?:\.\d+)?)/i);
    if (!match) {
      match = lower.match(/(-?\d+(?:\.\d+)?)[^a-z0-9]*(over|under)\b/i);
      if (match) {
        side = String(match[2]).toLowerCase();
        point = Number(match[1]);
      }
    } else {
      side = String(match[1]).toLowerCase();
      point = Number(match[2]);
    }

    if (!side) {
      const yesNoMatch = lower.match(/\b(yes|no)\b[^0-9-]*(-?\d+(?:\.\d+)?)/i);
      if (yesNoMatch) {
        side = String(yesNoMatch[1]).toLowerCase();
        point = Number(yesNoMatch[2]);
      }
    }

    if (!side) return;
    if (!Number.isFinite(point)) {
      const numeric = lower.match(/(-?\d+(?:\.\d+)?)/);
      if (numeric) point = Number(numeric[1]);
    }
    if (!Number.isFinite(point)) point = null;

    const sideIndex = lower.indexOf(side);
    if (sideIndex > 0) {
      participant = text.slice(0, sideIndex).replace(/[-:()]/g, " ").replace(/\s+/g, " ").trim();
    }
    if (!participant) {
      const chunks = text.split(/[-|:]/).map((chunk) => chunk.trim()).filter(Boolean);
      if (chunks.length > 1) {
        participant = chunks[0];
      }
    }

    if (lower.includes("assist") && lower.includes("rebound") && lower.includes("point")) marketHint = "pra";
    else if (lower.includes("assist") && lower.includes("point")) marketHint = "pa";
    else if (lower.includes("rebound") && lower.includes("point")) marketHint = "pr";
    else if (lower.includes("assist") && lower.includes("rebound")) marketHint = "ra";
    else if (lower.includes("assist")) marketHint = "assists";
    else if (lower.includes("rebound")) marketHint = "rebounds";
    else if (lower.includes("point")) marketHint = "points";
    else if (lower.includes("three")) marketHint = "threes";
    else if (lower.includes("block")) marketHint = "blocks";
    else if (lower.includes("steal")) marketHint = "steals";
    else if (lower.includes("turnover")) marketHint = "turnovers";
    else if (lower.includes("spread") || lower.includes("wins by")) marketHint = "spreads";
    else if (lower.includes("total")) marketHint = "totals";

    let marketClass = "generic";
    if (lower.includes("wins by") || lower.includes("spread")) marketClass = "team_spread";
    else if (
      lower.includes("total points") ||
      lower.includes("points scored") ||
      lower.includes("game total") ||
      lower.includes("total:")
    ) {
      marketClass = "game_total";
      participant = participant || "Game";
    } else if (
      lower.includes("player") ||
      lower.includes("to record") ||
      lower.includes("to score") ||
      lower.includes("points rebounds assists")
    ) {
      marketClass = "player_prop";
    }

    // Kalshi YES/NO contracts often encode directional side in label text.
    if ((side === "yes" || side === "no") && lower.includes("over")) {
      side = side === "yes" ? "over" : "under";
    } else if ((side === "yes" || side === "no") && lower.includes("under")) {
      side = side === "yes" ? "under" : "over";
    }

    const parsed = { side, point, participant, marketHint, marketClass, rawTitle: text };
    const score = (Number.isFinite(point) ? 1 : 0) + (participant ? 0.5 : 0) + (marketHint ? 0.5 : 0);
    if (!best || score > best.score) {
      best = { signal: parsed, score };
    }
  });

  return best ? best.signal : null;
}

function toMidProbability(sideBook = {}) {
  const bid = Number(sideBook?.bestBid);
  const ask = Number(sideBook?.bestAsk);
  const hasBid = Number.isFinite(bid);
  const hasAsk = Number.isFinite(ask);
  if (hasBid && hasAsk) return (bid + ask) / 2;
  if (hasBid) return bid;
  if (hasAsk) return ask;
  return null;
}

function candidateMarketKeyScore(marketKey = "", marketHint = "") {
  const key = String(marketKey || "").toLowerCase();
  if (!key) return 0;
  if (!marketHint) return 0.1;
  const byHint = {
    points: ["player_points", "team_totals", "totals"],
    rebounds: ["player_rebounds"],
    assists: ["player_assists"],
    threes: ["player_threes"],
    blocks: ["player_blocks"],
    steals: ["player_steals"],
    turnovers: ["player_turnovers"],
    pra: ["player_points_rebounds_assists"],
    pr: ["player_points_rebounds"],
    pa: ["player_points_assists"],
    ra: ["player_rebounds_assists"],
    spreads: ["spreads", "alternate_spreads"],
    totals: ["totals", "alternate_totals", "team_totals"],
  };
  const targets = byHint[marketHint] || [];
  if (!targets.length) return key.includes(marketHint) ? 0.2 : 0;
  if (targets.some((target) => key === target)) return 0.35;
  if (targets.some((target) => key.includes(target))) return 0.25;
  return 0;
}

function inferExchangeProbabilityForSignal(item = {}, signal = {}) {
  const yesMid = toMidProbability(item?.yes);
  const noMid = toMidProbability(item?.no);
  const hasYes = Number.isFinite(yesMid);
  const hasNo = Number.isFinite(noMid);
  if (!hasYes && !hasNo) return null;

  const yesLabel = String(item?.yesLabel || "").toLowerCase();
  const noLabel = String(item?.noLabel || "").toLowerCase();
  const title = String(item?.title || "").toLowerCase();
  const side = String(signal?.side || "").toLowerCase();

  if (side === "yes") return hasYes ? yesMid : 1 - noMid;
  if (side === "no") return hasNo ? noMid : 1 - yesMid;

  if (yesLabel.includes(side)) return hasYes ? yesMid : 1 - noMid;
  if (noLabel.includes(side)) return hasNo ? noMid : 1 - yesMid;

  if (side === "over") {
    if (title.includes("under") && !title.includes("over")) return hasNo ? noMid : 1 - yesMid;
    return hasYes ? yesMid : 1 - noMid;
  }
  if (side === "under") {
    if (title.includes("under") && !title.includes("over")) return hasYes ? yesMid : 1 - noMid;
    return hasNo ? noMid : 1 - yesMid;
  }

  return hasYes ? yesMid : 1 - noMid;
}

function isWinnerLikeExchangeMarket(item = {}) {
  const text = `${String(item?.title || "")} ${String(item?.yesLabel || "")} ${String(item?.noLabel || "")}`.toLowerCase();
  return text.includes("winner") || text.includes("to win") || text.includes("win series");
}

function resolveWinnerTeamName(item = {}) {
  const yesLabel = String(item?.yesLabel || "").trim();
  const noLabel = String(item?.noLabel || "").trim();
  if (yesLabel && yesLabel.toLowerCase() !== "yes" && yesLabel.toLowerCase() !== "no") return yesLabel;
  if (noLabel && noLabel.toLowerCase() !== "yes" && noLabel.toLowerCase() !== "no") return noLabel;

  const title = String(item?.title || "");
  const questionPattern = title.match(/who will win.*?-\s*(.+?)\s+vs\.?\s+(.+?)\s*$/i);
  if (questionPattern) return String(questionPattern[1] || "").trim();
  return "";
}

function resolveBookFairProbabilityForTeam(bookGame = {}, teamName = "") {
  const teamA = String(bookGame?.bookConsensus?.teamA?.name || bookGame?.teams?.[0] || "").trim();
  const teamB = String(bookGame?.bookConsensus?.teamB?.name || bookGame?.teams?.[1] || "").trim();
  const fairA = Number(bookGame?.bookConsensus?.teamA?.fairProbability);
  const fairB = Number(bookGame?.bookConsensus?.teamB?.fairProbability);

  const scoreA = overlapScore(teamName, teamA);
  const scoreB = overlapScore(teamName, teamB);
  if (scoreA <= 0 && scoreB <= 0) return null;
  if (scoreA >= scoreB) {
    return Number.isFinite(fairA) ? { probability: fairA, teamName: teamA } : null;
  }
  return Number.isFinite(fairB) ? { probability: fairB, teamName: teamB } : null;
}

function pickProbabilityForTeam(mapObj, teamName) {
  const map = mapObj || {};
  const exact = Object.entries(map).find(([name]) => normalizeTeamKey(name) === normalizeTeamKey(teamName));
  if (exact) return Number(exact[1]);

  const teamTokens = new Set(normalizeTeamKey(teamName).split(" ").filter(Boolean));
  const overlap = Object.entries(map).find(([name]) => {
    const rowTokens = new Set(normalizeTeamKey(name).split(" ").filter(Boolean));
    let score = 0;
    teamTokens.forEach((token) => {
      if (rowTokens.has(token)) score += 1;
    });
    return score > 0;
  });
  return overlap ? Number(overlap[1]) : null;
}

function edgeClassFromValue(edge) {
  const pct = Math.abs(Number(edge || 0) * 100);
  if (pct >= 8) return "delta-positive delta-strong";
  if (pct >= 4) return "delta-positive delta-medium";
  if (pct > 0) return "delta-positive delta-light";
  return "delta-zero";
}

function edgeBgStyle(edge) {
  const value = Math.abs(Number(edge || 0));
  if (!Number.isFinite(value)) return { background: "#eceff1", color: "#37474f" };
  if (value >= 0.1) return { background: "#1b5e20", color: "#fff", fontWeight: 700 };
  if (value >= 0.05) return { background: "#2e7d32", color: "#fff", fontWeight: 600 };
  if (value >= 0.01) return { background: "#43a0471f", color: "#1b5e20", fontWeight: 600 };
  return { background: "#eceff1", color: "#37474f" };
}

function exchangeLabel(market) {
  const source = String(market?.source || "").toLowerCase();
  if (source === "kalshi") return "Kalshi";
  return "Polymarket";
}

function exchangeGameUrl(market) {
  const source = String(market?.source || "").toLowerCase();
  if (source === "kalshi") {
    const eventTicker = String(market?.ticker || market?.marketId || "").trim();
    const seriesTicker = String(eventTicker.split("-")[0] || "").trim();
    if (seriesTicker && eventTicker) {
      return `https://kalshi.com/markets/${seriesTicker.toLowerCase()}/${eventTicker.toLowerCase()}`;
    }
    if (eventTicker) return `https://kalshi.com/markets/${eventTicker.toLowerCase()}`;
    return "https://kalshi.com/markets";
  }
  if (market?.slug) return `https://polymarket.com/event/${market.slug}`;
  return "https://polymarket.com";
}

function compareStartTimeAsc(a, b) {
  const aTime = Date.parse(a?.startTimeIso || a?.exchangeMarket?.startTimeIso || "");
  const bTime = Date.parse(b?.startTimeIso || b?.exchangeMarket?.startTimeIso || "");
  const safeA = Number.isFinite(aTime) ? aTime : Number.POSITIVE_INFINITY;
  const safeB = Number.isFinite(bTime) ? bTime : Number.POSITIVE_INFINITY;
  return safeA - safeB;
}

function edgePillStyle(edge) {
  const value = Math.abs(Number(edge || 0));
  if (value >= 0.1) {
    return { background: "#1b5e20", color: "#fff" };
  }
  if (value >= 0.05) {
    return { background: "#2e7d32", color: "#fff" };
  }
  if (value >= 0.01) {
    return { background: "#43a0471f", color: "#1b5e20" };
  }
  return { background: "#eceff1", color: "#37474f" };
}

function perBookProbabilityBreakdown(propComparison = null) {
  const candidate = propComparison?.candidate || {};
  const side = String(propComparison?.signal?.side || "").toLowerCase();
  const lines = Array.isArray(candidate?.lines) ? candidate.lines : [];
  if (!lines.length || !side) return [];

  return lines
    .map((line) => {
      const sideA = String(line?.sideAName || "").toLowerCase();
      const sideB = String(line?.sideBName || "").toLowerCase();
      const prob =
        sideA === side
          ? Number(line?.fairProbabilityA)
          : sideB === side
            ? Number(line?.fairProbabilityB)
            : null;
      if (!Number.isFinite(prob)) return null;
      const exchangeProb = Number(propComparison?.exchangeProb);
      const edge = Number.isFinite(exchangeProb) ? exchangeProb - prob : null;
      return {
        sportsbook: String(line?.sportsbook || "unknown"),
        probability: prob,
        edge,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.probability - b.probability);
}

function conservativeEdgeFromBreakdown(bookBreakdown = [], fallbackEdge = null) {
  const finiteEdges = (Array.isArray(bookBreakdown) ? bookBreakdown : [])
    .map((row) => Number(row?.edge))
    .filter((value) => Number.isFinite(value));
  if (finiteEdges.length) {
    return finiteEdges.reduce((best, current) => {
      if (!Number.isFinite(best)) return current;
      return Math.abs(current) < Math.abs(best) ? current : best;
    }, null);
  }
  const parsedFallback = Number(fallbackEdge);
  return Number.isFinite(parsedFallback) ? parsedFallback : null;
}

function totalOrderbookVolumeUsd(sideBook = {}) {
  const asks = Array.isArray(sideBook?.asks) ? sideBook.asks : [];
  const bids = Array.isArray(sideBook?.bids) ? sideBook.bids : [];
  return asks.concat(bids).reduce((sum, row) => sum + Number(row?.total || 0), 0);
}

function ownedContractsAtPrice(position = {}, side = "yes", price = null) {
  const fills = Array.isArray(position?.fills?.[side]) ? position.fills[side] : [];
  const target = Number(price);
  if (!Number.isFinite(target)) return 0;
  return fills.reduce((sum, fill) => {
    const fillPrice = Number(fill?.price);
    const qty = Number(fill?.qty);
    if (!Number.isFinite(fillPrice) || !Number.isFinite(qty)) return sum;
    if (Math.abs(fillPrice - target) < 0.00051) return sum + qty;
    return sum;
  }, 0);
}

function totalOwnedContracts(position = {}, side = "yes") {
  const fills = Array.isArray(position?.fills?.[side]) ? position.fills[side] : [];
  return fills.reduce((sum, fill) => sum + Number(fill?.qty || 0), 0);
}

function formatAgeShort(ageSeconds) {
  const seconds = Number(ageSeconds);
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function ownRestingAtPrice(item = {}, selectedSide = "yes", bookSide = "bid", price = null) {
  const side = String(selectedSide || "").toLowerCase() === "no" ? "no" : "yes";
  const ladderSide = String(bookSide || "").toLowerCase() === "ask" ? "ask" : "bid";
  const parsedPrice = Number(price);
  if (!Number.isFinite(parsedPrice)) return null;
  const priceKey = parsedPrice.toFixed(4);
  const bucket = item?.own?.resting?.[side]?.[ladderSide] || {};
  const row = bucket?.[priceKey];
  if (!row) return null;
  const qty = Number(row?.qty);
  if (!Number.isFinite(qty) || qty <= 0) return null;
  return {
    qty,
    ageSeconds: Number(row?.maxAgeSeconds),
    orderCount: Number(row?.orderCount || 0),
  };
}

function propsSummaryLabel(game) {
  const summary = game?.propsSummary || {};
  const total = Number(summary?.propMarketRows || 0);
  const playerTotal = Number(summary?.playerPropMarketRows || 0);
  const outcomes = Number(summary?.propOutcomeRows || 0);
  if (!total && !playerTotal) return "-";
  const keys = Array.isArray(summary?.uniquePropMarketKeys)
    ? summary.uniquePropMarketKeys.length
    : 0;
  return `${toCompactNumber(total)} mkts (${toCompactNumber(playerTotal)} player) • ${toCompactNumber(
    outcomes
  )} outs • ${keys} keys`;
}

function kalshiEventTickerForMarket(market) {
  return String(market?.ticker || market?.marketId || "").trim();
}

function kalshiEventUrl(eventTicker) {
  const normalizedEvent = String(eventTicker || "").trim();
  if (!normalizedEvent) return "https://kalshi.com/markets";
  const seriesTicker = String(normalizedEvent.split("-")[0] || "").trim();
  if (!seriesTicker) return `https://kalshi.com/markets/${normalizedEvent.toLowerCase()}`;
  return `https://kalshi.com/markets/${seriesTicker.toLowerCase()}/${normalizedEvent.toLowerCase()}`;
}

function polymarketEventSlugForMarket(market) {
  return String(market?.raw?.eventSlug || market?.eventSlug || market?.slug || "").trim();
}

function polymarketEventUrl(eventSlugOrMarketSlug) {
  const slug = String(eventSlugOrMarketSlug || "").trim();
  if (!slug) return "https://polymarket.com";
  return `https://polymarket.com/event/${slug}`;
}

const Dashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  const [matchedComparisons, setMatchedComparisons] = useState([]);
  const [sportsbookGames, setSportsbookGames] = useState([]);
  const [sportsbookScanMetadata, setSportsbookScanMetadata] = useState({});
  const [derivedJsonPopup, setDerivedJsonPopup] = useState(null);
  const [strategyPanelByTicker, setStrategyPanelByTicker] = useState({});
  const [strategyDraftByTicker, setStrategyDraftByTicker] = useState({});
  const [strategyTifPickerByTicker, setStrategyTifPickerByTicker] = useState({});
  const [paperPositionsByTicker, setPaperPositionsByTicker] = useState({});
  const [autoRefreshExchangeBooks, setAutoRefreshExchangeBooks] = useState(false);
  const [selectedExchangeGame, setSelectedExchangeGame] = useState(null);
  const [polymarketMarkets, setPolymarketMarkets] = useState([]);
  const [selectedPolymarketEventSlug, setSelectedPolymarketEventSlug] = useState("");
  const [polymarketGameDetails, setPolymarketGameDetails] = useState(null);
  const [polymarketOrderbookSideByTicker, setPolymarketOrderbookSideByTicker] = useState({});
  const polymarketLadderRefs = useRef({});
  const [isPolymarketGameLoading, setIsPolymarketGameLoading] = useState(false);
  const [kalshiMarkets, setKalshiMarkets] = useState([]);
  const [selectedKalshiEventTicker, setSelectedKalshiEventTicker] = useState("");
  const [kalshiGameDetails, setKalshiGameDetails] = useState(null);
  const [kalshiOrderbookSideByTicker, setKalshiOrderbookSideByTicker] = useState({});
  const kalshiLadderRefs = useRef({});
  const [isKalshiGameLoading, setIsKalshiGameLoading] = useState(false);
  const [isPlacingOrderByTicker, setIsPlacingOrderByTicker] = useState({});
  const [showOnlyMatchedGames, setShowOnlyMatchedGames] = useState(false);
  const [showOnlyGamesWithBooks, setShowOnlyGamesWithBooks] = useState(true);
  const [isSportsbookScanning, setIsSportsbookScanning] = useState(false);
  const [isPolymarketScanning, setIsPolymarketScanning] = useState(false);
  const [isKalshiScanning, setIsKalshiScanning] = useState(false);
  const [filters, setFilters] = useState({
    league: "",
    minEdge: "0",
    limit: "50",
  });

  const selectedExchangeSource = selectedPolymarketEventSlug
    ? "polymarket"
    : selectedKalshiEventTicker
      ? "kalshi"
      : "";

  const selectedExchangeDetails =
    selectedExchangeSource === "polymarket" ? polymarketGameDetails : kalshiGameDetails;
  const selectedExchangeKey =
    selectedExchangeSource === "polymarket" ? selectedPolymarketEventSlug : selectedKalshiEventTicker;
  const selectedExchangeLoading =
    selectedExchangeSource === "polymarket" ? isPolymarketGameLoading : isKalshiGameLoading;

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get("landmark/employees/login");
    } catch (error) {
      console.warn("employee login touch failed", error);
    }
  }, [isAuthenticated, employee]);

  useEffect(() => {
    if (!kalshiGameDetails?.items?.length) return;
    const frame = window.requestAnimationFrame(() => {
      Object.values(kalshiLadderRefs.current).forEach((container) => {
        if (!container) return;
        const midpoint = container.querySelector(".kalshi-ladder-midline");
        if (!midpoint) return;
        const targetScrollTop =
          midpoint.offsetTop - container.clientHeight / 2 + midpoint.clientHeight / 2;
        container.scrollTop = Math.max(0, targetScrollTop);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [kalshiGameDetails, kalshiOrderbookSideByTicker]);

  useEffect(() => {
    if (!polymarketGameDetails?.items?.length) return;
    const frame = window.requestAnimationFrame(() => {
      Object.values(polymarketLadderRefs.current).forEach((container) => {
        if (!container) return;
        const midpoint = container.querySelector(".kalshi-ladder-midline");
        if (!midpoint) return;
        const targetScrollTop =
          midpoint.offsetTop - container.clientHeight / 2 + midpoint.clientHeight / 2;
        container.scrollTop = Math.max(0, targetScrollTop);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [polymarketGameDetails, polymarketOrderbookSideByTicker]);

  useEffect(() => {
    if (!autoRefreshExchangeBooks) return undefined;
    if (!selectedExchangeSource) return undefined;
    let cancelled = false;
    const runRefresh = async () => {
      if (cancelled) return;
      await refreshSelectedExchangeDetails();
    };
    const timer = window.setInterval(runRefresh, 6000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    autoRefreshExchangeBooks,
    selectedExchangeSource,
    selectedPolymarketEventSlug,
    selectedKalshiEventTicker,
    employee,
  ]);

  const handleSetFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const ensureStrategyDraft = (ticker, item) => {
    setStrategyDraftByTicker((prev) => {
      if (prev?.[ticker]) return prev;
      const yesAsk = Number(item?.yes?.bestAsk);
      const noAsk = Number(item?.no?.bestAsk);
      return {
        ...prev,
        [ticker]: {
          linkQty: true,
          timeInForce: "good_till_canceled",
          yes: {
            price: clampNumber(Number.isFinite(yesAsk) ? yesAsk : 0.5, 0.01, 0.99),
            qty: 1,
          },
          no: {
            price: clampNumber(Number.isFinite(noAsk) ? noAsk : 0.5, 0.01, 0.99),
            qty: 1,
          },
        },
      };
    });
  };

  const toggleStrategyPanel = (ticker, item) => {
    ensureStrategyDraft(ticker, item);
    setStrategyPanelByTicker((prev) => ({
      ...prev,
      [ticker]: !prev?.[ticker],
    }));
  };

  const executePaperTrade = async (item, side) => {
    if (!employee) return;
    const ticker = String(item?.ticker || "").trim();
    if (!ticker) return;
    const draft = strategyDraftByTicker?.[ticker];
    if (!draft) return;
    const normalizedSide = String(side || "yes").toLowerCase() === "no" ? "no" : "yes";
    const sideDraft = draft?.[normalizedSide] || {};
    const price = Number(sideDraft?.price);
    const qty = Number(sideDraft?.qty);
    const timeInForce = String(draft?.timeInForce || "good_till_canceled").toLowerCase();
    if (!Number.isFinite(price) || price <= 0 || price >= 1) {
      toast.error("Enter a valid price between 0 and 1");
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Enter a valid contract quantity");
      return;
    }

    const placeKey = `${ticker}:${normalizedSide}`;
    setIsPlacingOrderByTicker((prev) => ({ ...prev, [placeKey]: true }));
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/prediction-bot/place-order", {
        source: selectedExchangeSource,
        ticker,
        side: normalizedSide,
        price,
        quantity: qty,
        timeInForce,
      });
      if (response?.error) {
        console.error("Prediction Bot place-order failed", response);
        toast.error(response.error);
        return;
      }
      if (!response?.success) {
        console.error("Prediction Bot place-order unexpected response", response);
        toast.error("Order placement failed");
        return;
      }

      const fillCount = Number(response?.response?.fill_count);
      const remainingCount = Number(response?.response?.remaining_count);
      const avgFill = Number(response?.response?.average_fill_price);
      const filledQty = Number.isFinite(fillCount) ? fillCount : qty;
      const fillPrice = Number.isFinite(avgFill) ? avgFill : price;
      const responseTimeInForce = String(response?.request?.timeInForce || "").toLowerCase();

      setPaperPositionsByTicker((prev) => {
        const existing = prev?.[ticker] || { fills: { yes: [], no: [] } };
        const nextFills = {
          yes: Array.isArray(existing?.fills?.yes) ? [...existing.fills.yes] : [],
          no: Array.isArray(existing?.fills?.no) ? [...existing.fills.no] : [],
        };
        if (filledQty > 0) {
          nextFills[normalizedSide].push({
            price: fillPrice,
            qty: filledQty,
            ts: Date.now(),
          });
        }
        return {
          ...prev,
          [ticker]: {
            ...existing,
            fills: nextFills,
          },
        };
      });

      const orderId = response?.response?.order_id || response?.response?.orderId || "";
      let fillNote = "order accepted";
      const hasFill = Number.isFinite(fillCount) && fillCount > 0;
      const hasRemaining = Number.isFinite(remainingCount) && remainingCount > 0;
      if (responseTimeInForce === "immediate_or_cancel") {
        if (hasFill) {
          fillNote = `filled ${toContracts(fillCount)} (IOC)`;
        } else {
          fillNote = "IOC unfilled, canceled";
        }
      } else if (hasFill && hasRemaining) {
        fillNote = `partially filled ${toContracts(fillCount)}, resting ${toContracts(remainingCount)}`;
      } else if (hasFill) {
        fillNote = `filled ${toContracts(fillCount)}`;
      } else if (hasRemaining) {
        fillNote = `resting ${toContracts(remainingCount)}`;
      }
      const orderSuffix = orderId ? ` • ${orderId}` : "";
      toast.success(`${normalizedSide.toUpperCase()} order submitted (${fillNote})${orderSuffix}`);
      await refreshSelectedExchangeDetails();
    } catch (error) {
      console.error("Prediction Bot place-order request exception", error);
      toast.error("Failed to place live order");
    } finally {
      setIsPlacingOrderByTicker((prev) => ({ ...prev, [placeKey]: false }));
    }
  };

  const refreshSelectedExchangeDetails = async () => {
    if (!employee) return;
    if (selectedExchangeSource === "polymarket" && selectedPolymarketEventSlug) {
      try {
        const api = new ApiService({ employee });
        const response = await api.post(
          "landmark/prediction-bot/polymarket-game-details",
          { eventSlug: selectedPolymarketEventSlug, marketSlug: "" },
          null,
          { timeout: 120000 }
        );
        if (response?.success) setPolymarketGameDetails(response);
      } catch (error) {
        // silent in auto refresh mode
      }
      return;
    }
    if (selectedExchangeSource === "kalshi" && selectedKalshiEventTicker) {
      try {
        const api = new ApiService({ employee });
        const response = await api.post("landmark/prediction-bot/kalshi-game-details", {
          eventTicker: selectedKalshiEventTicker,
        });
        if (response?.success) setKalshiGameDetails(response);
      } catch (error) {
        // silent in auto refresh mode
      }
    }
  };

  const runMatchScan = async (api) => {
    const response = await api.post("landmark/prediction-bot/scan-matches", {});
    if (response?.error) {
      toast.error(response.error);
      return;
    }
    setMatchedComparisons(response?.items || []);
  };

  const handleSportsbookScan = async (forceRefresh = false) => {
    if (!employee) return;
    setIsSportsbookScanning(true);
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/prediction-bot/scan-sportsbooks", {
        refresh: forceRefresh,
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.success === false) {
        toast.error((response?.errors || ["Sportsbook scan failed"]).join(", "));
      } else {
        const propRows = Number(response?.metadata?.totalPropMarketRows || 0);
        const cacheUsed = Boolean(response?.metadata?.cache?.used);
        const cacheLabel = cacheUsed ? ", cached" : "";
        if (propRows > 0) {
          toast.success(
            `Sportsbook scan complete (${(response?.items || []).length} games, ${toCompactNumber(
              propRows
            )} prop markets${cacheLabel})`
          );
        } else {
          toast.success(`Sportsbook scan complete (${(response?.items || []).length} games${cacheLabel})`);
        }
      }
      setSportsbookGames(response?.items || []);
      setSportsbookScanMetadata(response?.metadata || {});
      await runMatchScan(api);
    } catch (error) {
      toast.error("Failed to scan sportsbook providers");
    } finally {
      setIsSportsbookScanning(false);
    }
  };

  const handlePolymarketScan = async () => {
    if (!employee) return;
    setIsPolymarketScanning(true);
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/prediction-bot/scan-polymarket", {});
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.success === false) {
        toast.error((response?.errors || ["Polymarket scan failed"]).join(", "));
      } else {
        toast.success(`Polymarket scan complete (${(response?.items || []).length} markets)`);
      }
      setPolymarketMarkets(response?.items || []);
      setSelectedPolymarketEventSlug("");
      setPolymarketGameDetails(null);
      setPolymarketOrderbookSideByTicker({});
      setSelectedExchangeGame(null);
      await runMatchScan(api);
    } catch (error) {
      toast.error("Failed to scan Polymarket");
    } finally {
      setIsPolymarketScanning(false);
    }
  };

  const handleKalshiScan = async () => {
    if (!employee) return;
    setIsKalshiScanning(true);
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/prediction-bot/scan-kalshi", {}, null, {
        timeout: 120000,
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.success === false) {
        toast.error((response?.errors || ["Kalshi scan failed"]).join(", "));
      } else {
        toast.success(`Kalshi scan complete (${(response?.items || []).length} markets)`);
      }
      setKalshiMarkets(response?.items || []);
      setSelectedExchangeGame(null);
      await runMatchScan(api);
    } catch (error) {
      toast.error("Failed to scan Kalshi");
    } finally {
      setIsKalshiScanning(false);
    }
  };

  const loadKalshiGameDetails = async (market) => {
    if (!employee) return;
    const eventTicker = kalshiEventTickerForMarket(market);
    if (!eventTicker) {
      toast.error("Missing Kalshi event ticker for selected game");
      return;
    }

    setSelectedKalshiEventTicker(eventTicker);
    setSelectedPolymarketEventSlug("");
    setPolymarketGameDetails(null);
    setSelectedExchangeGame({
      source: "kalshi",
      teams: Array.isArray(market?.teams) ? market.teams : [],
      startTimeIso: market?.startTimeIso || "",
    });
    setIsKalshiGameLoading(true);
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/prediction-bot/kalshi-game-details", {
        eventTicker,
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.success === false) {
        toast.error((response?.errors || ["Failed to load Kalshi game details"]).join(", "));
        return;
      }
      setKalshiGameDetails(response);
      setKalshiOrderbookSideByTicker({});
      if (Array.isArray(response?.errors) && response.errors.length) {
        toast((response.errors || []).slice(0, 2).join(" | "));
      } else {
        toast.success(
          `Loaded ${response?.marketCount || (response?.items || []).length} Kalshi markets for ${eventTicker}`
        );
      }
    } catch (error) {
      toast.error("Failed to load Kalshi game details");
    } finally {
      setIsKalshiGameLoading(false);
    }
  };

  const loadPolymarketGameDetails = async (market) => {
    if (!employee) return;
    const eventSlug = polymarketEventSlugForMarket(market);
    const marketSlug = String(market?.slug || "").trim();
    if (!eventSlug && !marketSlug) {
      toast.error("Missing Polymarket event slug for selected game");
      return;
    }

    setSelectedPolymarketEventSlug(eventSlug || marketSlug);
    setSelectedKalshiEventTicker("");
    setKalshiGameDetails(null);
    setSelectedExchangeGame({
      source: "polymarket",
      teams: Array.isArray(market?.teams) ? market.teams : [],
      startTimeIso: market?.startTimeIso || "",
    });
    setIsPolymarketGameLoading(true);
    try {
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/prediction-bot/polymarket-game-details",
        { eventSlug, marketSlug },
        null,
        { timeout: 120000 }
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.success === false) {
        toast.error((response?.errors || ["Failed to load Polymarket game details"]).join(", "));
        return;
      }
      setPolymarketGameDetails(response);
      setPolymarketOrderbookSideByTicker({});
      if (Array.isArray(response?.errors) && response.errors.length) {
        toast((response.errors || []).slice(0, 2).join(" | "));
      } else {
        toast.success(
          `Loaded ${response?.marketCount || (response?.items || []).length} Polymarket markets for ${
            response?.eventSlug || eventSlug || marketSlug
          }`
        );
      }
    } catch (error) {
      toast.error("Failed to load Polymarket game details");
    } finally {
      setIsPolymarketGameLoading(false);
    }
  };

  const leagues = useMemo(() => {
    const unique = new Set(
      []
        .concat(sportsbookGames.map((row) => row.league))
        .concat(polymarketMarkets.map((row) => row.league))
        .concat(kalshiMarkets.map((row) => row.league))
        .concat(matchedComparisons.map((row) => row.league))
        .filter(Boolean)
    );
    return Array.from(unique).sort();
  }, [sportsbookGames, polymarketMarkets, kalshiMarkets, matchedComparisons]);

  const filteredSportsbookGames = useMemo(() => {
    return sportsbookGames.filter((game) => {
      if (showOnlyMatchedGames && !game.hasExchangeMatch) return false;
      if (showOnlyGamesWithBooks && Number(game?.bookConsensus?.bookCount || 0) <= 0) {
        return false;
      }
      return true;
    });
  }, [sportsbookGames, showOnlyMatchedGames, showOnlyGamesWithBooks]);

  const sortedSportsbookGames = useMemo(() => {
    return [...filteredSportsbookGames].sort(compareStartTimeAsc);
  }, [filteredSportsbookGames]);

  const hasPotentiallyScrambledSportsbookData = useMemo(() => {
    if (Array.isArray(sportsbookScanMetadata?.providersWithSuspectedScramble)) {
      if (sportsbookScanMetadata.providersWithSuspectedScramble.length > 0) return true;
    }
    return sortedSportsbookGames.some((game) => Boolean(game?.dataQuality?.suspectScrambled));
  }, [sportsbookScanMetadata, sortedSportsbookGames]);

  const sortedPolymarketMarkets = useMemo(() => {
    return [...polymarketMarkets].sort(compareStartTimeAsc);
  }, [polymarketMarkets]);

  const sortedKalshiMarkets = useMemo(() => {
    return [...kalshiMarkets].sort(compareStartTimeAsc);
  }, [kalshiMarkets]);

  const matchedSportsbookGameForSelection = useMemo(() => {
    if (!selectedExchangeGame || !Array.isArray(selectedExchangeGame?.teams) || selectedExchangeGame.teams.length < 2) {
      return null;
    }
    const targetTeams = selectedExchangeGame.teams;
    const targetStart = Date.parse(selectedExchangeGame?.startTimeIso || "");
    let best = null;
    let bestScore = 0;

    sortedSportsbookGames.forEach((game) => {
      const gameTeams = Array.isArray(game?.teams) ? game.teams : [];
      if (gameTeams.length < 2) return;
      const teamScoreA = overlapScore(targetTeams[0], gameTeams[0]);
      const teamScoreB = overlapScore(targetTeams[1], gameTeams[1]);
      const teamScoreSwappedA = overlapScore(targetTeams[0], gameTeams[1]);
      const teamScoreSwappedB = overlapScore(targetTeams[1], gameTeams[0]);
      const teamScore = Math.max((teamScoreA + teamScoreB) / 2, (teamScoreSwappedA + teamScoreSwappedB) / 2);

      const gameStart = Date.parse(game?.startTimeIso || "");
      let timeScore = 0.5;
      if (Number.isFinite(targetStart) && Number.isFinite(gameStart)) {
        const diffMinutes = Math.abs(targetStart - gameStart) / 60000;
        timeScore = Math.max(0, 1 - diffMinutes / 360);
      }

      const score = teamScore * 0.8 + timeScore * 0.2;
      if (score > bestScore) {
        best = game;
        bestScore = score;
      }
    });

    return bestScore >= 0.45 ? best : null;
  }, [selectedExchangeGame, sortedSportsbookGames]);

  const selectedGamePointsMarketSummary = useMemo(() => {
    const propMarkets = Array.isArray(matchedSportsbookGameForSelection?.propMarkets)
      ? matchedSportsbookGameForSelection.propMarkets
      : [];
    const pointsRows = propMarkets.filter((row) => {
      const key = String(row?.marketKey || "").toLowerCase();
      return key.includes("totals") || key.includes("points");
    });
    const byKey = {};
    pointsRows.forEach((row) => {
      const key = String(row?.marketKey || "unknown");
      if (!byKey[key]) byKey[key] = { count: 0, lines: [] };
      byKey[key].count += 1;
      if (byKey[key].lines.length < 12 && Number.isFinite(Number(row?.point))) {
        byKey[key].lines.push(Number(row.point));
      }
    });
    Object.values(byKey).forEach((entry) => {
      entry.lines = Array.from(new Set(entry.lines)).sort((a, b) => a - b);
    });
    return {
      totalRows: pointsRows.length,
      byKey,
    };
  }, [matchedSportsbookGameForSelection]);

  const getPropComparisonForMarket = (item, selectedSide = "yes") => {
    if (matchedSportsbookGameForSelection && isWinnerLikeExchangeMarket(item)) {
      const winnerTeam = resolveWinnerTeamName(item);
      const winnerFair = resolveBookFairProbabilityForTeam(matchedSportsbookGameForSelection, winnerTeam);
      const yesMid = toMidProbability(item?.yes);
      const noMid = toMidProbability(item?.no);
      const exchangeProb =
        selectedSide === "no"
          ? Number.isFinite(noMid)
            ? noMid
            : Number.isFinite(yesMid)
              ? 1 - yesMid
              : null
          : Number.isFinite(yesMid)
            ? yesMid
            : Number.isFinite(noMid)
              ? 1 - noMid
              : null;
      if (winnerFair && Number.isFinite(exchangeProb)) {
        const sportsbookFair = selectedSide === "no" ? 1 - winnerFair.probability : winnerFair.probability;
        return {
          signal: {
            side: selectedSide === "no" ? "no" : "yes",
            point: null,
            participant: winnerFair.teamName,
            marketHint: "winner",
          },
          candidate: {
            marketKey: "moneyline",
            participant: winnerFair.teamName,
            point: null,
            books: Number(matchedSportsbookGameForSelection?.bookConsensus?.bookCount || 0),
          },
          sportsbookFair,
          exchangeProb,
          edge: exchangeProb - sportsbookFair,
          confidence: 0.98,
        };
      }
    }

    const propMarkets = Array.isArray(matchedSportsbookGameForSelection?.propMarkets)
      ? matchedSportsbookGameForSelection.propMarkets
      : [];
    if (!propMarkets.length) return null;
    const signal = parseExchangePropSignal(item?.title || "", [
      item?.yesLabel || "",
      item?.noLabel || "",
      `${item?.title || ""} ${item?.yesLabel || ""} ${item?.noLabel || ""}`.trim(),
    ]);
    if (!signal) return null;

    let best = null;
    let bestScore = 0;
    propMarkets.forEach((candidate) => {
      const candidateKey = String(candidate?.marketKey || "").toLowerCase();
      const sideA = String(candidate?.sideAName || "").toLowerCase();
      const sideB = String(candidate?.sideBName || "").toLowerCase();
      if (sideA !== signal.side && sideB !== signal.side) return;

      const signalClass = String(signal?.marketClass || "generic");
      if (signalClass === "team_spread") {
        if (!candidateKey.includes("spread")) return;
      } else if (signalClass === "game_total") {
        if (!candidateKey.includes("total")) return;
      } else if (signalClass === "player_prop") {
        if (!candidateKey.startsWith("player_")) return;
      }

      const candidatePoint = Number(candidate?.point);
      const signalPoint = Number(signal?.point);
      const pointDiff =
        Number.isFinite(candidatePoint) && Number.isFinite(signalPoint)
          ? Math.abs(candidatePoint - signalPoint)
          : 0.75;
      const tolerance = String(candidate?.marketKey || "").includes("alternate_") ? 4 : 2.5;
      if (Number.isFinite(candidatePoint) && Number.isFinite(signalPoint) && pointDiff > tolerance) return;
      const pointScore =
        Number.isFinite(candidatePoint) && Number.isFinite(signalPoint)
          ? Math.max(0, 1 - pointDiff / tolerance)
          : 0.45;

      let participantScore = signal.participant
        ? overlapScore(signal.participant, candidate?.participant || "")
        : 0.15;
      if (signalClass === "game_total") {
        const candidateParticipant = String(candidate?.participant || "").toLowerCase();
        if (candidateParticipant === "game" || candidateParticipant.includes("game")) {
          participantScore = Math.max(participantScore, 0.9);
        }
      }
      if (signalClass === "team_spread" && participantScore < 0.3) return;

      const hintScore = candidateMarketKeyScore(candidateKey, signal?.marketHint);
      const bookScore = Math.min(0.25, Number(candidate?.books || 0) / 12);
      const sideConsistencyScore =
        (sideA === signal.side ? 0.1 : 0) + (sideB === signal.side ? 0.05 : 0);

      const score =
        participantScore * 0.5 +
        pointScore * 0.28 +
        hintScore +
        bookScore +
        sideConsistencyScore;
      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    });

    if (!best || bestScore < 0.45) return null;
    const sportsbookFair =
      String(best?.sideAName || "").toLowerCase() === signal.side
        ? Number(best?.fairProbabilityA)
        : Number(best?.fairProbabilityB);
    if (!Number.isFinite(sportsbookFair)) return null;

    const exchangeProb = inferExchangeProbabilityForSignal(item, signal);
    if (!Number.isFinite(exchangeProb)) return null;
    const edge = Number.isFinite(exchangeProb) ? exchangeProb - sportsbookFair : null;
    return {
      signal,
      candidate: best,
      sportsbookFair,
      exchangeProb,
      edge,
      confidence: bestScore,
    };
  };

  const selectedMatchedMarketTableRows = useMemo(() => {
    const items = Array.isArray(selectedExchangeDetails?.items) ? selectedExchangeDetails.items : [];
    if (!items.length) return [];
    return items.map((item) => {
      const yes = item?.yes || {};
      const no = item?.no || {};
      const yesMatch = getPropComparisonForMarket(item, "yes");
      const noMatch = getPropComparisonForMarket(item, "no");
      let fairYes = null;
      if (Number.isFinite(Number(yesMatch?.sportsbookFair))) {
        fairYes = Number(yesMatch.sportsbookFair);
      } else if (Number.isFinite(Number(noMatch?.sportsbookFair))) {
        fairYes = 1 - Number(noMatch.sportsbookFair);
      }

      const yesAsk = Number(yes?.bestAsk);
      const noAsk = Number(no?.bestAsk);
      const askSpread =
        Number.isFinite(yesAsk) && Number.isFinite(noAsk) ? Number((yesAsk - noAsk).toFixed(4)) : null;

      return {
        league: normalizeLeagueLabel(
          selectedExchangeSource === "polymarket"
            ? matchedSportsbookGameForSelection?.league || "Basketball"
            : matchedSportsbookGameForSelection?.league || "NBA"
        ),
        game: selectedExchangeDetails?.title || selectedExchangeKey || "-",
        market: item?.title || item?.ticker || "-",
        yesAsk: yes?.bestAsk ?? null,
        yesBid: yes?.bestBid ?? null,
        noAsk: no?.bestAsk ?? null,
        noBid: no?.bestBid ?? null,
        askSpread,
        volume: item?.volume ?? 0,
        fairEstimateYes: fairYes,
      };
    });
  }, [
    selectedExchangeDetails,
    selectedExchangeSource,
    selectedExchangeKey,
    matchedSportsbookGameForSelection,
  ]);

  const openDerivedJsonPopup = (event, { item, propComparison, selectedSide, bookBreakdown = [] }) => {
    if (!propComparison) return;
    const payload = {
      exchangeMarket: {
        ticker: item?.ticker,
        title: item?.title,
        yesLabel: item?.yesLabel,
        noLabel: item?.noLabel,
        selectedSide,
        yes: item?.yes,
        no: item?.no,
      },
      matchedSportsbookGame: matchedSportsbookGameForSelection
        ? {
            gameId: matchedSportsbookGameForSelection?.gameId,
            league: matchedSportsbookGameForSelection?.league,
            teams: matchedSportsbookGameForSelection?.teams,
            startTimeIso: matchedSportsbookGameForSelection?.startTimeIso,
            bookConsensus: matchedSportsbookGameForSelection?.bookConsensus,
          }
        : null,
      matchSignal: propComparison?.signal || null,
      matchedCandidate: propComparison?.candidate || null,
      computed: {
        sportsbookFair: propComparison?.sportsbookFair,
        exchangeProb: propComparison?.exchangeProb,
        averageEdge: propComparison?.edge,
        conservativeEdge: conservativeEdgeFromBreakdown(bookBreakdown, propComparison?.edge),
        confidence: propComparison?.confidence,
      },
      perBookBreakdown: bookBreakdown,
    };

    const viewportWidth = window.innerWidth || 1200;
    const viewportHeight = window.innerHeight || 800;
    const popupWidth = 560;
    const popupHeight = 420;
    const x = Math.min(Math.max(12, event.clientX + 8), Math.max(12, viewportWidth - popupWidth - 12));
    const y = Math.min(Math.max(12, event.clientY + 8), Math.max(12, viewportHeight - popupHeight - 12));
    setDerivedJsonPopup({
      x,
      y,
      payload,
      json: JSON.stringify(payload, null, 2),
    });
  };

  const matchedRows = useMemo(() => {
    return matchedComparisons.map((row) => {
      const teamA = row?.teams?.[0] || row?.bookConsensus?.teamA?.name || "Team A";
      const teamB = row?.teams?.[1] || row?.bookConsensus?.teamB?.name || "Team B";

      const fairA = pickProbabilityForTeam(row?.metrics?.fairProbabilities, teamA);
      const fairB = pickProbabilityForTeam(row?.metrics?.fairProbabilities, teamB);
      const marketA = pickProbabilityForTeam(row?.metrics?.marketProbabilities, teamA);
      const marketB = pickProbabilityForTeam(row?.metrics?.marketProbabilities, teamB);

      const edgeA = Number.isFinite(marketA) && Number.isFinite(fairA) ? marketA - fairA : null;
      const edgeB = Number.isFinite(marketB) && Number.isFinite(fairB) ? marketB - fairB : null;
      const absEdgeA = Number.isFinite(edgeA) ? Math.abs(edgeA) : null;
      const absEdgeB = Number.isFinite(edgeB) ? Math.abs(edgeB) : null;
      const displayEdge = Math.max(
        Number.isFinite(absEdgeA) ? absEdgeA : Number.NEGATIVE_INFINITY,
        Number.isFinite(absEdgeB) ? absEdgeB : Number.NEGATIVE_INFINITY
      );

      return {
        ...row,
        teamA,
        teamB,
        fairA,
        fairB,
        marketA,
        marketB,
        edgeA,
        edgeB,
        displayEdge: Number.isFinite(displayEdge) ? displayEdge : null,
      };
    });
  }, [matchedComparisons]);

  const sortedMatchedRows = useMemo(() => {
    return [...matchedRows].sort(compareStartTimeAsc);
  }, [matchedRows]);

  const buildProviderOddsSummary = (game) => {
    const lines = Array.isArray(game?.bookConsensus?.lines) ? game.bookConsensus.lines : [];
    if (!lines.length) return "-";
    const grouped = {};
    lines.forEach((line) => {
      const provider = String(line?.provider || "unknown");
      if (!grouped[provider]) grouped[provider] = [];
      grouped[provider].push(line);
    });
    return Object.entries(grouped)
      .map(([provider, providerLines]) => {
        const first = providerLines[0] || {};
        const teamA = first?.teamAName || game?.teams?.[0] || "A";
        const teamB = first?.teamBName || game?.teams?.[1] || "B";
        const oddsA = first?.rawOdds?.[0] ?? "-";
        const oddsB = first?.rawOdds?.[1] ?? "-";
        return `${provider} (${providerLines.length}): ${teamA} ${oddsA} | ${teamB} ${oddsB}`;
      })
      .join(" || ");
  };

  useDocumentTitle("Prediction Bot - Landmark Ventures");

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper prediction-bot-dashboard">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: "" },
            { title: "Prediction Bot", url: "/landmark/predictionbot" },
          ]}
        />

        {!isAuthenticated ? (
          <div className="text-center text-muted p-5">
            Please sign in to access the prediction bot dashboard.
          </div>
        ) : (
          <>
            <Card className="mb-3">
              <Card.Body>
                <Row className="g-2 align-items-end">
                  <Col md={4}>
                    <label className="form-label">League</label>
                    <select
                      className="form-control"
                      value={filters.league}
                      onChange={(e) => handleSetFilter("league", e.target.value)}
                    >
                      <option value="">All leagues</option>
                      {leagues.map((league) => (
                        <option key={league} value={league}>
                          {league}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col md={4}>
                    <label className="form-label">Minimum edge</label>
                    <input
                      className="form-control"
                      value={filters.minEdge}
                      onChange={(e) => handleSetFilter("minEdge", e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <label className="form-label">Limit</label>
                    <input
                      className="form-control"
                      value={filters.limit}
                      onChange={(e) => handleSetFilter("limit", e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <label className="form-label d-block">Sportsbook list</label>
                    <div className="form-check mt-2">
                      <input
                        id="showOnlyMatchedGames"
                        className="form-check-input"
                        type="checkbox"
                        checked={showOnlyMatchedGames}
                        onChange={(e) => setShowOnlyMatchedGames(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="showOnlyMatchedGames">
                        Show only games with exchange match
                      </label>
                    </div>
                    <div className="form-check mt-2">
                      <input
                        id="showOnlyGamesWithBooks"
                        className="form-check-input"
                        type="checkbox"
                        checked={showOnlyGamesWithBooks}
                        onChange={(e) => setShowOnlyGamesWithBooks(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="showOnlyGamesWithBooks">
                        Show only games with books
                      </label>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row className="g-3 mb-3">
              <Col lg={12}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Card.Title className="mb-0 d-flex align-items-center gap-2">
                        <span>Sportsbook Scan</span>
                        {hasPotentiallyScrambledSportsbookData && (
                          <span className="scrambled-data-badge" title="One or more sportsbook feeds appears to be trial/scrambled data">
                            Possible scrambled data
                          </span>
                        )}
                      </Card.Title>
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">
                          {isSportsbookScanning
                            ? "Scanning..."
                            : `${sortedSportsbookGames.length}/${sportsbookGames.length} games`}
                        </small>
                        {!isSportsbookScanning && Boolean(sportsbookScanMetadata?.cache?.used) && (
                          <small className="text-muted me-2">Using cached odds</small>
                        )}
                        <button
                          className="btn btn-outline-primary btn-sm me-2"
                          onClick={() => handleSportsbookScan(false)}
                          disabled={isSportsbookScanning}
                        >
                          {isSportsbookScanning ? "Scanning..." : "Load Sportsbooks"}
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSportsbookScan(true)}
                          disabled={isSportsbookScanning}
                          title="Fetch fresh data from The Odds API and overwrite cache"
                        >
                          {isSportsbookScanning ? "Scanning..." : "Refresh Live"}
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: "auto", overflowX: "auto" }}>
                      <Table
                        responsive
                        hover
                        className="scan-table mb-0"
                        style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "0.82rem" }}
                      >
                        <thead>
                          <tr>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>League</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Game</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Start</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Providers</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Props</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedSportsbookGames.map((game) => {
                            const teamA = game?.bookConsensus?.teamA?.name || game?.teams?.[0] || "";
                            const teamB = game?.bookConsensus?.teamB?.name || game?.teams?.[1] || "";
                            return (
                              <tr key={`${game.runId}-${game.gameId}`} style={{ background: "#fff" }}>
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {normalizeLeagueLabel(game.league)}
                                </td>
                                <td
                                  style={{
                                    padding: "7px 10px",
                                    borderBottom: "1px solid #f0f2f5",
                                    fontWeight: 500,
                                    maxWidth: 220,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={`${teamA} vs ${teamB}`}
                                >
                                  {`${teamA} vs ${teamB}`}
                                </td>
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {game.startTimeIso ? new Date(game.startTimeIso).toLocaleString() : "-"}
                                </td>
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <span>
                                      {Array.isArray(game?.providers) && game.providers.length
                                        ? game.providers.map((provider) => toProviderLabel(provider)).join(", ")
                                        : "-"}
                                    </span>
                                    {game?.dataQuality?.suspectScrambled && (
                                      <span
                                        className="scrambled-data-badge"
                                        title={`Detected from ${Array.isArray(game?.dataQuality?.providersWithSuspectedScramble) ? game.dataQuality.providersWithSuspectedScramble.join(", ") : "provider signals"}`}
                                      >
                                        Scrambled?
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: "7px 10px",
                                    borderBottom: "1px solid #f0f2f5",
                                    whiteSpace: "nowrap",
                                    maxWidth: 280,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={propsSummaryLabel(game)}
                                >
                                  {propsSummaryLabel(game)}
                                </td>
                              </tr>
                            );
                          })}
                          {!filteredSportsbookGames.length && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted p-4">
                                No sportsbook games match current filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Card.Title className="mb-0">
                        <img
                          src={polymarketLogo}
                          alt="Polymarket"
                          className="prediction-exchange-logo prediction-exchange-logo-polymarket"
                        />
                      </Card.Title>
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">
                          {isPolymarketScanning ? "Scanning..." : `${polymarketMarkets.length} markets`}
                        </small>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handlePolymarketScan}
                          disabled={isPolymarketScanning}
                        >
                          {isPolymarketScanning ? "Scanning..." : "Scan Polymarket"}
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: "auto", overflowX: "auto" }}>
                      <Table
                        responsive
                        hover
                        className="scan-table mb-0"
                        style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "0.82rem" }}
                      >
                        <thead>
                          <tr>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>League</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Game</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Start</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPolymarketMarkets.map((market) => {
                            const teamA = market?.outcomes?.[0]?.name || market?.teams?.[0] || "";
                            const teamB = market?.outcomes?.[1]?.name || market?.teams?.[1] || "";
                            const gameLabel = `${teamA} vs ${teamB}`;
                            const eventSlug = polymarketEventSlugForMarket(market) || market?.slug || "";
                            const isSelected = selectedPolymarketEventSlug === eventSlug;
                            return (
                              <tr
                                key={market.marketId || market.slug}
                                className={`exchange-selectable-row ${isSelected ? "exchange-selected" : ""}`}
                                style={{ background: isSelected ? "#e8f2ff" : "#fff" }}
                                onClick={() => loadPolymarketGameDetails(market)}
                                title="Click row to view all related Polymarket markets and orderbooks"
                              >
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {normalizeLeagueLabel(market.league)}
                                </td>
                                <td
                                  style={{
                                    padding: "7px 10px",
                                    borderBottom: "1px solid #f0f2f5",
                                    fontWeight: 500,
                                    maxWidth: 240,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={gameLabel}
                                >
                                  <a
                                    href={exchangeGameUrl(market)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="exchange-link"
                                  >
                                    {gameLabel}
                                  </a>
                                </td>
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {market.startTimeIso ? new Date(market.startTimeIso).toLocaleString() : "-"}
                                </td>
                              </tr>
                            );
                          })}
                          {!polymarketMarkets.length && (
                            <tr>
                              <td colSpan={3} className="text-center text-muted p-4">
                                Run a Polymarket scan to load markets.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Card.Title className="mb-0">
                        <img
                          src={kalshiLogo}
                          alt="Kalshi"
                          className="prediction-exchange-logo prediction-exchange-logo-kalshi"
                        />
                      </Card.Title>
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">
                          {isKalshiScanning ? "Scanning..." : `${kalshiMarkets.length} markets`}
                        </small>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleKalshiScan}
                          disabled={isKalshiScanning}
                        >
                          {isKalshiScanning ? "Scanning..." : "Scan Kalshi"}
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: "auto", overflowX: "auto" }}>
                      <Table
                        responsive
                        hover
                        className="scan-table mb-0"
                        style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "0.82rem" }}
                      >
                        <thead>
                          <tr>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>League</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Game</th>
                            <th style={{ background: "#f7f8fa", padding: "8px 10px", borderBottom: "1px solid #eceef2" }}>Start</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedKalshiMarkets.map((market) => {
                            const teamA = market?.outcomes?.[0]?.name || market?.teams?.[0] || "";
                            const teamB = market?.outcomes?.[1]?.name || market?.teams?.[1] || "";
                            const gameLabel = `${teamA} vs ${teamB}`;
                            const eventTicker = kalshiEventTickerForMarket(market);
                            const isSelected = selectedKalshiEventTicker === eventTicker;
                            return (
                              <tr
                                key={market.marketId || market.slug}
                                className={`kalshi-selectable-row ${isSelected ? "kalshi-selected" : ""}`}
                                style={{ background: isSelected ? "#e8f2ff" : "#fff" }}
                                onClick={() => loadKalshiGameDetails(market)}
                                title="Click row to view all related Kalshi markets and orderbooks"
                              >
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {normalizeLeagueLabel(market.league)}
                                </td>
                                <td
                                  style={{
                                    padding: "7px 10px",
                                    borderBottom: "1px solid #f0f2f5",
                                    fontWeight: 500,
                                    maxWidth: 240,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={gameLabel}
                                >
                                  <a
                                    href={exchangeGameUrl(market)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="exchange-link"
                                  >
                                    {gameLabel}
                                  </a>
                                </td>
                                <td style={{ padding: "7px 10px", borderBottom: "1px solid #f0f2f5", whiteSpace: "nowrap" }}>
                                  {market.startTimeIso ? new Date(market.startTimeIso).toLocaleString() : "-"}
                                </td>
                              </tr>
                            );
                          })}
                          {!kalshiMarkets.length && (
                            <tr>
                              <td colSpan={3} className="text-center text-muted p-4">
                                Run a Kalshi scan to load markets.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Card.Title className="mb-0">Selected Game Markets + Orderbooks</Card.Title>
                  <div className="d-flex align-items-center gap-3">
                    {selectedExchangeKey && (
                      <label className="d-flex align-items-center mb-0" style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        <input
                          type="checkbox"
                          className="me-1"
                          checked={autoRefreshExchangeBooks}
                          onChange={(e) => setAutoRefreshExchangeBooks(e.target.checked)}
                        />
                        Auto refresh books (6s)
                      </label>
                    )}
                    <small className="text-muted">
                      {selectedExchangeKey
                        ? `${selectedExchangeSource === "polymarket" ? "Polymarket" : "Kalshi"} • ${selectedExchangeKey}`
                        : "Select a game from either exchange table"}
                    </small>
                  </div>
                </div>

                {!selectedExchangeKey && (
                  <div className="text-muted">
                    Click a Polymarket or Kalshi game row to inspect markets, orderbooks, and prop edge comparisons.
                  </div>
                )}

                {selectedExchangeKey && selectedExchangeLoading && (
                  <div className="text-muted">
                    Loading {selectedExchangeSource === "polymarket" ? "Polymarket" : "Kalshi"} market ladders...
                  </div>
                )}

                {selectedExchangeKey && !selectedExchangeLoading && selectedExchangeDetails && (
                  <div>
                    <div className="mb-2" style={{ fontWeight: 600 }}>
                      {selectedExchangeDetails?.title || selectedExchangeKey}
                    </div>
                    {matchedSportsbookGameForSelection?.propMarkets?.length > 0 ? (
                      <div className="mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                        Matched sportsbook props: {toCompactNumber(matchedSportsbookGameForSelection?.propsSummary?.propMarketRows || 0)} rows
                        from {toCompactNumber(matchedSportsbookGameForSelection?.propsSummary?.bookCountWithProps || 0)} books
                      </div>
                    ) : (
                      <div className="mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                        No sportsbook props matched for this game yet.
                      </div>
                    )}
                    {Array.isArray(selectedExchangeDetails?.errors) && selectedExchangeDetails.errors.length > 0 && (
                      <div className="mb-3 text-warning" style={{ fontSize: "0.8rem" }}>
                        {selectedExchangeDetails.errors.slice(0, 3).join(" | ")}
                      </div>
                    )}

                    <Row className="g-3 kalshi-orderbook-grid">
                      {(selectedExchangeDetails?.items || []).map((item) => {
                        const sideState = selectedExchangeSource === "polymarket"
                          ? polymarketOrderbookSideByTicker
                          : kalshiOrderbookSideByTicker;
                        const setSideState = selectedExchangeSource === "polymarket"
                          ? setPolymarketOrderbookSideByTicker
                          : setKalshiOrderbookSideByTicker;
                        const selectedSide = sideState[item.ticker] || "yes";
                        const sideBook = selectedSide === "yes" ? item?.yes : item?.no;
                        const bidRows = Array.isArray(sideBook?.bids) ? sideBook.bids : [];
                        const askRows = Array.isArray(sideBook?.asks) ? sideBook.asks : [];
                        const askRowsForDisplay = [...askRows].sort((a, b) => b.price - a.price);
                        const bidRowsForDisplay = [...bidRows].sort((a, b) => b.price - a.price);
                        const askCumulativeTotals = buildCumulativeTotals(askRowsForDisplay, "suffix");
                        const bidCumulativeTotals = buildCumulativeTotals(bidRowsForDisplay, "prefix");
                        const askDepthPercents = buildDepthPercentsFromCumulative(askCumulativeTotals);
                        const bidDepthPercents = buildDepthPercentsFromCumulative(bidCumulativeTotals);
                        const contractLabel =
                          selectedSide === "yes"
                            ? item?.yesLabel || "YES contract"
                            : item?.noLabel || "NO contract";
                        const propComparison = getPropComparisonForMarket(item, selectedSide);
                        const bookBreakdown = perBookProbabilityBreakdown(propComparison);
                        const conservativeEdge = conservativeEdgeFromBreakdown(
                          bookBreakdown,
                          propComparison?.edge
                        );
                        const edgeStyle = edgePillStyle(conservativeEdge || 0);
                        const orderbookTotalUsd = totalOrderbookVolumeUsd(sideBook);
                        const strategyDraft = strategyDraftByTicker?.[item.ticker] || {
                          linkQty: true,
                          timeInForce: "good_till_canceled",
                          yes: {
                            price: Number.isFinite(Number(item?.yes?.bestAsk)) ? Number(item?.yes?.bestAsk) : 0.5,
                            qty: 1,
                          },
                          no: {
                            price: Number.isFinite(Number(item?.no?.bestAsk)) ? Number(item?.no?.bestAsk) : 0.5,
                            qty: 1,
                          },
                        };
                        const position = paperPositionsByTicker?.[item.ticker] || { fills: { yes: [], no: [] } };
                        const ownedYes = totalOwnedContracts(position, "yes");
                        const ownedNo = totalOwnedContracts(position, "no");
                        const exchangePositionFp = Number(item?.own?.position?.positionFp);
                        const exchangeRestingCount = Number(item?.own?.position?.restingOrdersCount);
                        const exchangePositionLabel = Number.isFinite(exchangePositionFp)
                          ? exchangePositionFp > 0
                            ? `Exchange pos: YES ${toContracts(exchangePositionFp)}`
                            : exchangePositionFp < 0
                              ? `Exchange pos: NO ${toContracts(Math.abs(exchangePositionFp))}`
                              : "Exchange pos: Flat"
                          : "";

                        return (
                          <Col lg={6} key={item.ticker}>
                            <Card className="exchange-orderbook-card kalshi-orderbook-card h-100">
                              <Card.Body style={{ padding: "12px" }}>
                                <div className="exchange-orderbook-header kalshi-orderbook-header">
                                  <div className="exchange-orderbook-title kalshi-orderbook-title">{item.title || item.ticker}</div>
                                  <a
                                    href={
                                      selectedExchangeSource === "polymarket"
                                        ? polymarketEventUrl(item.eventTicker || selectedExchangeKey)
                                        : kalshiEventUrl(item.eventTicker || selectedExchangeKey)
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    className="exchange-link kalshi-open-link"
                                  >
                                    Open game page
                                  </a>
                                </div>
                                <div className="exchange-contract-line kalshi-contract-line">Contract: {contractLabel}</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem", marginBottom: 6 }}>
                                  Position: YES {toContracts(ownedYes)} | NO {toContracts(ownedNo)}
                                </div>
                                {exchangePositionLabel && (
                                  <div className="text-muted" style={{ fontSize: "0.72rem", marginBottom: 6 }}>
                                    {exchangePositionLabel}
                                    {Number.isFinite(exchangeRestingCount) && exchangeRestingCount > 0
                                      ? ` • Resting ${toContracts(exchangeRestingCount)}`
                                      : ""}
                                  </div>
                                )}

                                <div className="exchange-orderbook-toolbar kalshi-orderbook-toolbar">
                                  <div className="exchange-orderbook-toggle-group kalshi-orderbook-toggle-group">
                                    <button
                                      className={`exchange-side-toggle kalshi-side-toggle ${selectedSide === "yes" ? "active" : ""}`}
                                      onClick={() =>
                                        setSideState((prev) => ({
                                          ...prev,
                                          [item.ticker]: "yes",
                                        }))
                                      }
                                    >
                                      Trade Yes
                                    </button>
                                    <button
                                      className={`exchange-side-toggle kalshi-side-toggle ${selectedSide === "no" ? "active" : ""}`}
                                      onClick={() =>
                                        setSideState((prev) => ({
                                          ...prev,
                                          [item.ticker]: "no",
                                        }))
                                      }
                                    >
                                      Trade No
                                    </button>
                                    <button
                                      className={`exchange-side-toggle kalshi-side-toggle ${
                                        strategyPanelByTicker?.[item.ticker] ? "active" : ""
                                      }`}
                                      onClick={() => toggleStrategyPanel(item.ticker, item)}
                                    >
                                      Strategy
                                    </button>
                                  </div>
                                  <div className="exchange-top-chips kalshi-top-chips">
                                    <span className="exchange-top-chip kalshi-top-chip kalshi-top-chip-yes">
                                      Yes {toCents(item?.yes?.bestAsk)}
                                    </span>
                                    <span className="exchange-top-chip kalshi-top-chip kalshi-top-chip-no">
                                      No {toCents(item?.no?.bestAsk)}
                                    </span>
                                    <span className="exchange-top-chip kalshi-top-chip">
                                      {selectedSide.toUpperCase()} spread {toPrice(sideBook?.spread)}
                                    </span>
                                  </div>
                                </div>

                                {strategyPanelByTicker?.[item.ticker] && (
                                  <div className="strategy-panel">
                                    <div className="strategy-panel-link-wrap">
                                      <div className="strategy-panel-link-controls">
                                        <button
                                          type="button"
                                          className={`strategy-link-toggle ${strategyDraft?.linkQty ? "active" : ""}`}
                                          title="Link YES/NO contract quantities"
                                          onClick={() =>
                                            setStrategyDraftByTicker((prev) => ({
                                              ...prev,
                                              [item.ticker]: {
                                                ...(prev?.[item.ticker] || strategyDraft),
                                                linkQty: !Boolean((prev?.[item.ticker] || strategyDraft)?.linkQty),
                                              },
                                            }))
                                          }
                                        >
                                          🔗 {strategyDraft?.linkQty ? "Linked Qty" : "Unlinked Qty"}
                                        </button>
                                        {strategyTifPickerByTicker?.[item.ticker] ? (
                                          <label className="strategy-tif-label">
                                            Time in force
                                            <select
                                              className="form-control form-control-sm"
                                              value={strategyDraft?.timeInForce || "good_till_canceled"}
                                              autoFocus
                                              onBlur={() =>
                                                setStrategyTifPickerByTicker((prev) => ({
                                                  ...prev,
                                                  [item.ticker]: false,
                                                }))
                                              }
                                              onChange={(e) =>
                                                setStrategyDraftByTicker((prev) => ({
                                                  ...prev,
                                                  [item.ticker]: {
                                                    ...(prev?.[item.ticker] || strategyDraft),
                                                    timeInForce: e.target.value,
                                                  },
                                                }))
                                              }
                                            >
                                              <option value="good_till_canceled">Limit (GTC)</option>
                                              <option value="immediate_or_cancel">Immediate or cancel (IOC)</option>
                                              <option value="fill_or_kill">Fill or kill (FOK)</option>
                                            </select>
                                          </label>
                                        ) : (
                                          <button
                                            type="button"
                                            className="strategy-tif-text-btn"
                                            onClick={() =>
                                              setStrategyTifPickerByTicker((prev) => ({
                                                ...prev,
                                                [item.ticker]: true,
                                              }))
                                            }
                                          >
                                            Order type:{" "}
                                            <strong>
                                              {strategyDraft?.timeInForce === "immediate_or_cancel"
                                                ? "Immediate or cancel (IOC)"
                                                : strategyDraft?.timeInForce === "fill_or_kill"
                                                  ? "Fill or kill (FOK)"
                                                  : "Limit (GTC)"}
                                            </strong>
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {["yes", "no"].map((tradeSide) => {
                                      const sideDraft = strategyDraft?.[tradeSide] || {};
                                      const currentPrice = clampNumber(
                                        Number(sideDraft?.price),
                                        0.01,
                                        0.99
                                      );
                                      const currentQty = Math.max(
                                        1,
                                        Math.round(Number(sideDraft?.qty || 1))
                                      );
                                      const placeKey = `${item.ticker}:${tradeSide}`;
                                      return (
                                        <div key={`${item.ticker}-strategy-${tradeSide}`} className="strategy-side-row">
                                          <div className={`strategy-side-label ${tradeSide === "yes" ? "yes" : "no"}`}>
                                            {tradeSide.toUpperCase()}
                                          </div>
                                          <div className="strategy-drag-col">
                                            <label>Price</label>
                                            <DraggableNumber
                                              value={currentPrice}
                                              min={0.01}
                                              max={0.99}
                                              step={0.01}
                                              parseInput={(raw) => Number(raw)}
                                              formatter={(value) => `${Number(value).toFixed(2)}`}
                                              className="strategy-drag-value strategy-drag-price"
                                              title="Drag up/down. Double-click to type."
                                              onChange={(nextPrice) =>
                                                setStrategyDraftByTicker((prev) => ({
                                                  ...prev,
                                                  [item.ticker]: {
                                                    ...(prev?.[item.ticker] || strategyDraft),
                                                    [tradeSide]: {
                                                      ...((prev?.[item.ticker] || strategyDraft)?.[tradeSide] || sideDraft),
                                                      price: clampNumber(nextPrice, 0.01, 0.99),
                                                    },
                                                  },
                                                }))
                                              }
                                            />
                                          </div>

                                          <div className="strategy-drag-col">
                                            <label>Contracts</label>
                                            <DraggableNumber
                                              value={currentQty}
                                              min={1}
                                              max={100000}
                                              step={10}
                                              parseInput={(raw) => Math.max(1, Math.round(Number(raw) || 1))}
                                              formatter={(value) => `${Math.round(Number(value))}`}
                                              className="strategy-drag-value strategy-drag-qty"
                                              title="Drag up/down by 10. Double-click to type."
                                              onChange={(nextQtyRaw) =>
                                                setStrategyDraftByTicker((prev) => {
                                                  const prevTicker = prev?.[item.ticker] || strategyDraft;
                                                  const linked = Boolean(prevTicker?.linkQty);
                                                  const nextQty = Math.max(1, Math.round(Number(nextQtyRaw) || 1));
                                                  const nextTicker = {
                                                    ...prevTicker,
                                                    [tradeSide]: {
                                                      ...(prevTicker?.[tradeSide] || sideDraft),
                                                      qty: nextQty,
                                                    },
                                                  };
                                                  if (linked) {
                                                    const otherSide = tradeSide === "yes" ? "no" : "yes";
                                                    nextTicker[otherSide] = {
                                                      ...(prevTicker?.[otherSide] || {}),
                                                      qty: nextQty,
                                                    };
                                                  }
                                                  return {
                                                    ...prev,
                                                    [item.ticker]: nextTicker,
                                                  };
                                                })
                                              }
                                            />
                                          </div>

                                          <button
                                            className="btn btn-sm btn-primary strategy-execute-btn"
                                            onClick={() => executePaperTrade(item, tradeSide)}
                                            disabled={Boolean(isPlacingOrderByTicker?.[placeKey])}
                                          >
                                            {isPlacingOrderByTicker?.[placeKey]
                                              ? "Placing..."
                                              : `Buy ${tradeSide.toUpperCase()}`}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                <div
                                  className="exchange-ladder-wrap kalshi-ladder-wrap"
                                  ref={(el) => {
                                    const refs = selectedExchangeSource === "polymarket" ? polymarketLadderRefs : kalshiLadderRefs;
                                    if (el) refs.current[item.ticker] = el;
                                    else delete refs.current[item.ticker];
                                  }}
                                >
                                  <div className="exchange-ladder-meta">
                                    <span>Total orderbook: {toUsd(orderbookTotalUsd)}</span>
                                  </div>
                                  <Table size="sm" className="mb-0 exchange-ladder-table kalshi-ladder-table">
                                    <colgroup>
                                      <col style={{ width: "34%" }} />
                                      <col style={{ width: "18%" }} />
                                      <col style={{ width: "30%" }} />
                                      <col style={{ width: "18%" }} />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th>Side</th>
                                        <th>Price</th>
                                        <th>Contracts</th>
                                        <th>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {askRowsForDisplay.map((ask, idx) => {
                                        const showAskBadge = idx === askRowsForDisplay.length - 1;
                                        const ownedQty = ownedContractsAtPrice(position, selectedSide, ask?.price);
                                        const resting = ownRestingAtPrice(item, selectedSide, "ask", ask?.price);
                                        return (
                                          <tr
                                            key={`${item.ticker}-${selectedSide}-ask-${idx}`}
                                            className={`asks-row ${ownedQty > 0 ? "owned-order-row" : ""}`}
                                          >
                                            <td className="depth-label-cell">
                                              <div
                                                className="depth-fill depth-fill-ask"
                                                style={{ width: `${askDepthPercents[idx] || 0}%` }}
                                              />
                                              {showAskBadge ? (
                                                <span className="depth-label-text">
                                                  <span className="side-badge side-badge-ask">Asks</span>
                                                  {ownedQty > 0 && <span className="side-badge side-badge-owned">Own {toContracts(ownedQty)}</span>}
                                                  {resting && (
                                                    <span className="side-badge side-badge-resting">
                                                      <span>{toContracts(resting.qty)}</span>
                                                      <span className="resting-clock">⏱</span>
                                                      <span>{formatAgeShort(resting.ageSeconds)}</span>
                                                    </span>
                                                  )}
                                                </span>
                                              ) : (
                                                <span className="depth-label-text">
                                                  {ownedQty > 0 ? <span className="side-badge side-badge-owned">Own {toContracts(ownedQty)}</span> : null}
                                                  {resting ? (
                                                    <span className="side-badge side-badge-resting">
                                                      <span>{toContracts(resting.qty)}</span>
                                                      <span className="resting-clock">⏱</span>
                                                      <span>{formatAgeShort(resting.ageSeconds)}</span>
                                                    </span>
                                                  ) : null}
                                                  {!ownedQty && !resting ? <>&nbsp;</> : null}
                                                </span>
                                              )}
                                            </td>
                                            <td>{toCents(ask?.price)}</td>
                                            <td>{toContracts(ask?.contracts)}</td>
                                            <td>{toUsd(askCumulativeTotals[idx])}</td>
                                          </tr>
                                        );
                                      })}
                                      {Boolean(askRowsForDisplay.length || bidRowsForDisplay.length) && (
                                        <tr>
                                          <td colSpan={4} className="kalshi-ladder-midline">
                                            <div className="kalshi-ladder-midline-content">
                                              <span className="kalshi-ladder-midline-left">
                                                Last:{" "}
                                                {toCents(
                                                  selectedSide === "yes"
                                                    ? item?.yes?.bestBid ?? item?.yes?.bestAsk
                                                    : item?.no?.bestBid ?? item?.no?.bestAsk
                                                )}
                                              </span>
                                              <span className="kalshi-ladder-midline-center">
                                                Spread: {toCents(sideBook?.spread)}
                                              </span>
                                              <span className="kalshi-ladder-midline-right">&nbsp;</span>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                      {bidRowsForDisplay.map((bid, idx) => {
                                        const showBidBadge = idx === 0;
                                        const ownedQty = ownedContractsAtPrice(position, selectedSide, bid?.price);
                                        const resting = ownRestingAtPrice(item, selectedSide, "bid", bid?.price);
                                        return (
                                          <tr
                                            key={`${item.ticker}-${selectedSide}-bid-${idx}`}
                                            className={`bids-row ${ownedQty > 0 ? "owned-order-row" : ""}`}
                                          >
                                            <td className="depth-label-cell">
                                              <div
                                                className="depth-fill depth-fill-bid"
                                                style={{ width: `${bidDepthPercents[idx] || 0}%` }}
                                              />
                                              {showBidBadge ? (
                                                <span className="depth-label-text">
                                                  <span className="side-badge side-badge-bid">Bids</span>
                                                  {ownedQty > 0 && <span className="side-badge side-badge-owned">Own {toContracts(ownedQty)}</span>}
                                                  {resting && (
                                                    <span className="side-badge side-badge-resting">
                                                      <span>{toContracts(resting.qty)}</span>
                                                      <span className="resting-clock">⏱</span>
                                                      <span>{formatAgeShort(resting.ageSeconds)}</span>
                                                    </span>
                                                  )}
                                                </span>
                                              ) : (
                                                <span className="depth-label-text">
                                                  {ownedQty > 0 ? <span className="side-badge side-badge-owned">Own {toContracts(ownedQty)}</span> : null}
                                                  {resting ? (
                                                    <span className="side-badge side-badge-resting">
                                                      <span>{toContracts(resting.qty)}</span>
                                                      <span className="resting-clock">⏱</span>
                                                      <span>{formatAgeShort(resting.ageSeconds)}</span>
                                                    </span>
                                                  ) : null}
                                                  {!ownedQty && !resting ? <>&nbsp;</> : null}
                                                </span>
                                              )}
                                            </td>
                                            <td>{toCents(bid?.price)}</td>
                                            <td>{toContracts(bid?.contracts)}</td>
                                            <td>{toUsd(bidCumulativeTotals[idx])}</td>
                                          </tr>
                                        );
                                      })}
                                      {!askRowsForDisplay.length && !bidRowsForDisplay.length && (
                                        <tr>
                                          <td colSpan={4} className="text-center text-muted">
                                            No {selectedSide.toUpperCase()} orderbook depth available
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </Table>
                                </div>

                                <div className="prop-edge-panel mt-2">
                                  {propComparison ? (
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div style={{ fontSize: "0.78rem" }}>
                                        <div>
                                          <button
                                            type="button"
                                            className="prop-json-trigger"
                                            onClick={(evt) =>
                                              openDerivedJsonPopup(evt, {
                                                item,
                                                propComparison,
                                                selectedSide,
                                                bookBreakdown,
                                              })
                                            }
                                            title="View exact JSON inputs used for this fair probability and edge"
                                          >
                                            Sportsbook fair ({propComparison.signal.side.toUpperCase()} {propComparison.candidate?.point ?? "-"}):{" "}
                                            {toPercent(propComparison.sportsbookFair)}
                                          </button>
                                        </div>
                                        {bookBreakdown.length > 0 && (
                                          <div className="book-breakdown-grid">
                                            {bookBreakdown.map((row) => (
                                              <div key={`${item.ticker}-${row.sportsbook}`} className="book-breakdown-col">
                                                <div className="book-breakdown-prob">
                                                  {row.sportsbook}: {toPercent(row.probability)}
                                                </div>
                                                <span
                                                  className="book-breakdown-edge-badge"
                                                  style={{
                                                    ...edgePillStyle(row.edge),
                                                    borderRadius: 999,
                                                    padding: "1px 8px",
                                                    fontSize: "0.68rem",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {toAbsPercent(row.edge)} edge
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div>
                                          Exchange implied YES: {toPercent(propComparison.exchangeProb)}
                                        </div>
                                      </div>
                                      <span
                                        style={{
                                          ...edgeStyle,
                                          borderRadius: 999,
                                          padding: "2px 10px",
                                          fontSize: "0.74rem",
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {toAbsPercent(conservativeEdge)} edge
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                      No sportsbook prop line match found for this market yet.
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                      {!selectedExchangeDetails?.items?.length && (
                        <div className="text-muted">No market ladders returned for this game.</div>
                      )}
                    </Row>

                    {selectedMatchedMarketTableRows.length > 0 && (
                      <div className="mt-3">
                        <div className="mb-2" style={{ fontWeight: 600 }}>
                          Matched Market Snapshot
                        </div>
                        <div style={{ maxHeight: 300, overflowY: "auto", overflowX: "auto" }}>
                          <Table
                            responsive
                            className="scan-table mb-0"
                            style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "0.78rem" }}
                          >
                            <thead>
                              <tr>
                                <th>League</th>
                                <th>Game</th>
                                <th>Market</th>
                                <th>Yes Ask</th>
                                <th>Yes Bid</th>
                                <th>No Ask</th>
                                <th>No Bid</th>
                                <th>Ask Spread (Y-N)</th>
                                <th>Volume</th>
                                <th>Fair Estimate (YES)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedMatchedMarketTableRows.map((row, idx) => (
                                <tr key={`${row.market}-${idx}`}>
                                  <td style={{ whiteSpace: "nowrap" }}>{row.league}</td>
                                  <td style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.game}>
                                    {row.game}
                                  </td>
                                  <td style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.market}>
                                    {row.market}
                                  </td>
                                  <td>{toCents(row.yesAsk)}</td>
                                  <td>{toCents(row.yesBid)}</td>
                                  <td>{toCents(row.noAsk)}</td>
                                  <td>{toCents(row.noBid)}</td>
                                  <td>{toCents(row.askSpread)}</td>
                                  <td>{toCompactUsd(row.volume)}</td>
                                  <td>{toPercent(row.fairEstimateYes)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>

            {derivedJsonPopup && (
              <div
                className="derived-json-backdrop"
                onClick={() => setDerivedJsonPopup(null)}
                role="presentation"
              >
                <div
                  className="derived-json-popover"
                  style={{ left: `${derivedJsonPopup.x}px`, top: `${derivedJsonPopup.y}px` }}
                  onClick={(evt) => evt.stopPropagation()}
                  role="dialog"
                  aria-label="Derived sportsbook JSON data"
                >
                  <div className="derived-json-popover-header">
                    <strong>Derived JSON (source inputs)</strong>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setDerivedJsonPopup(null)}
                    >
                      Close
                    </button>
                  </div>
                  <pre className="derived-json-popover-body">{derivedJsonPopup.json}</pre>
                </div>
              </div>
            )}

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Card.Title className="mb-0">Matched Games (Sportsbook vs Exchanges)</Card.Title>
                  <small className="text-muted">{`${matchedRows.length} rows`}</small>
                </div>
                <div style={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
                <Table responsive className="matched-table mb-0" style={{ fontSize: "0.84rem", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th>League</th>
                      <th>Game</th>
                      <th>Start</th>
                      <th>Volume</th>
                      <th>Liquidity</th>
                      <th>Team Odds Comparison</th>
                      <th>Edge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMatchedRows.map((row) => {
                      const marketLabel = row?.exchangeMarket?.slug || row?.exchangeMarket?.marketId || "-";
                      const marketSource = exchangeLabel(row?.exchangeMarket);
                      const rowStart = row?.exchangeMarket?.startTimeIso || row?.startTimeIso;
                      return (
                        <tr key={`${row.runId}-${row.canonicalGameId}`}>
                          <td>{normalizeLeagueLabel(row.league)}</td>
                          <td style={{ fontWeight: 500 }}>
                            <a
                              href={exchangeGameUrl(row?.exchangeMarket)}
                              target="_blank"
                              rel="noreferrer"
                              className="exchange-link"
                            >
                              {(row.teams || []).join(" vs ")}
                            </a>
                            <div className="text-muted" style={{ fontSize: "0.74rem" }}>
                              {marketSource}: {marketLabel}
                            </div>
                          </td>
                          <td>{rowStart ? new Date(rowStart).toLocaleString() : "-"}</td>
                          <td>{toCompactUsd(row?.exchangeMarket?.volume || 0)}</td>
                          <td>{toCompactUsd(row?.exchangeMarket?.liquidity || 0)}</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <div className="d-flex flex-column gap-1">
                                <div>
                                  <strong>{row.teamA}</strong>: sportsbook {toPercent(row.fairA)} |{" "}
                                  {marketSource.toLowerCase()}{" "}
                                  {toPercent(row.marketA)}
                                </div>
                                <div>
                                  <strong>{row.teamB}</strong>: sportsbook {toPercent(row.fairB)} |{" "}
                                  {marketSource.toLowerCase()}{" "}
                                  {toPercent(row.marketB)}
                                </div>
                              </div>
                              <span
                                style={{
                                  ...edgePillStyle(row.displayEdge),
                                  borderRadius: 999,
                                  padding: "2px 10px",
                                  fontSize: "0.74rem",
                                  whiteSpace: "nowrap",
                                  display: "inline-block",
                                  alignSelf: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {`${toAbsPercent(row.displayEdge)} Δ`}
                              </span>
                            </div>
                          </td>
                          <td
                            className={`date-col-body edge-cell ${edgeClassFromValue(row?.displayEdge)}`}
                            style={edgeBgStyle(row?.displayEdge)}
                          >
                            <div>{toAbsPercent(row?.displayEdge)}</div>
                          </td>
                        </tr>
                      );
                    })}
                    {!sortedMatchedRows.length && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted p-4">
                          No matched sportsbook/exchange entries yet for current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Dashboard;
